import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, CheckCircle, Shield, Building2, Users,
  MessageCircle, Phone, Mail, ChevronDown, Loader2
} from 'lucide-react';

const ROLES = ['Eigentümer', 'Hausverwaltung', 'Bestandshalter', 'Dienstleister', 'Sonstiges'];
const UNITS = ['1–10', '11–50', '51–200', '201–500', '500+'];
const PROBLEMS = [
  'Dokumentenchaos',
  'Mieteingangskontrolle',
  'Nebenkostenabrechnung',
  'Softwarewechsel',
  'Kommunikation mit Mietern',
  'Reporting / Überblick',
];
const NEXT_STEPS = [
  'Demo vereinbaren',
  'Rückruf anfragen',
  'Dokument prüfen lassen',
  'Bestand importieren',
  'WhatsApp-Kontakt',
];

const PilotRequestPage = () => {
  const [step, setStep] = useState('form'); // 'form' | 'success'
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    role: '',
    units: '',
    current_software: '',
    main_problem: '',
    desired_next_step: '',
    website: '', // Honeypot — muss leer bleiben
  });

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const res = await fetch('/api/leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: form.phone || null,
          company: form.company || null,
          role: form.role || null,
          units: form.units || null,
          current_software: form.current_software || null,
          main_problem: form.main_problem || null,
          desired_next_step: form.desired_next_step || null,
          consent: true,
          website: form.website, // Honeypot
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || `HTTP ${res.status}`);
      }

      setStep('success');
    } catch (err) {
      console.error('Lead submit error:', err.message);
      setError('Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut oder schreiben Sie uns direkt.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass = "w-full px-4 py-3 rounded-lg text-sm text-white placeholder-gray-500 border focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-colors";
  const inputStyle = { background: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.12)' };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-4 py-12 relative overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 40%, #312e81 70%, #1e1b4b 100%)' }}
    >
      {/* Hintergrund-Orbs */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/6 w-96 h-96 bg-indigo-600/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/6 w-80 h-80 bg-violet-600/10 rounded-full blur-3xl" />
      </div>

      {/* Logo */}
      <div className="relative z-10 mb-8 flex items-center gap-3">
        <img src="/clara360-logo.png" alt="Clara360" className="h-9 w-auto" />
        <span className="text-white font-semibold text-lg tracking-tight">Clara360</span>
      </div>

      {step === 'success' ? (
        /* ── Danke-Seite ── */
        <div
          className="relative z-10 w-full max-w-lg rounded-2xl p-8 text-center"
          style={{
            background: 'rgba(255,255,255,0.06)',
            backdropFilter: 'blur(24px)',
            WebkitBackdropFilter: 'blur(24px)',
            border: '1px solid rgba(255,255,255,0.10)',
            boxShadow: '0 8px 48px rgba(99,102,241,0.18)',
          }}
        >
          <div className="w-14 h-14 rounded-full bg-indigo-500/20 flex items-center justify-center mx-auto mb-5">
            <CheckCircle className="w-7 h-7 text-indigo-400" />
          </div>
          <h2 className="text-white font-semibold text-xl mb-3">Anfrage eingegangen</h2>
          <p className="text-gray-300 text-sm leading-relaxed mb-6">
            Wir prüfen Ihre Anfrage und melden uns mit dem passenden Einstieg.
            Uploads und Datenübernahmen erfolgen erst nach bewusster Freigabe.
          </p>

          <div className="space-y-3 mb-6">
            <a
              href="https://wa.me/491709782676?text=Hallo%20Clara%2C%20ich%20habe%20gerade%20eine%20Pilotanfrage%20gestellt%20und%20m%C3%B6chte%20mehr%20erfahren."
              target="_blank" rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full py-3 rounded-lg text-sm font-medium text-white transition-colors"
              style={{ background: 'rgba(37,211,102,0.15)', border: '1px solid rgba(37,211,102,0.3)' }}
            >
              <MessageCircle className="w-4 h-4 text-green-400" />
              <span className="text-green-300">Direkt per WhatsApp kontaktieren</span>
            </a>

            <p className="text-gray-600 text-xs">
              Sie können optional schon Unterlagen vorbereiten (Excel, PDF, CSV, ZIP) —
              der Upload erfolgt erst nach Freigabe.
            </p>
          </div>

          <Link
            to="/login"
            className="text-indigo-400 hover:text-indigo-300 text-sm transition-colors"
          >
            ← Zurück zur Startseite
          </Link>
        </div>
      ) : (
        /* ── Lead-Formular ── */
        <div className="relative z-10 w-full max-w-2xl">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium mb-4"
              style={{ background: 'rgba(99,102,241,0.15)', border: '1px solid rgba(99,102,241,0.3)', color: '#a5b4fc' }}>
              <Shield className="w-3 h-3" />
              Geführter Pilotbetrieb
            </div>
            <h1 className="text-white font-bold text-2xl md:text-3xl leading-tight mb-3">
              Pilotzugang anfragen
            </h1>
            <p className="text-gray-400 text-sm max-w-md mx-auto leading-relaxed">
              Clara360 befindet sich im geführten Pilotbetrieb. Wir starten neue Bestände
              kontrolliert, damit Datenqualität, Datenschutz und Automatisierung sauber eingerichtet werden.
            </p>
          </div>

          {/* Formular-Card */}
          <div
            className="rounded-2xl p-7 md:p-8"
            style={{
              background: 'rgba(255,255,255,0.06)',
              backdropFilter: 'blur(24px)',
              WebkitBackdropFilter: 'blur(24px)',
              border: '1px solid rgba(255,255,255,0.10)',
              boxShadow: '0 8px 48px rgba(99,102,241,0.18)',
            }}
          >
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Honeypot — für Bots unsichtbar */}
              <input
                type="text"
                name="website"
                value={form.website}
                onChange={handleChange}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, width: 0 }}
              />
              {/* Name + E-Mail */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wider">
                    Name <span className="text-indigo-400">*</span>
                  </label>
                  <div className="relative">
                    <Users className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                    <input
                      type="text" name="name" value={form.name} onChange={handleChange}
                      required placeholder="Ihr Name"
                      className={inputClass + " pl-9"} style={inputStyle}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wider">
                    E-Mail <span className="text-indigo-400">*</span>
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                    <input
                      type="email" name="email" value={form.email} onChange={handleChange}
                      required placeholder="ihre@email.de"
                      className={inputClass + " pl-9"} style={inputStyle}
                    />
                  </div>
                </div>
              </div>

              {/* Telefon + Unternehmen */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wider">
                    Telefon <span className="text-gray-600 normal-case font-normal">(optional)</span>
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                    <input
                      type="tel" name="phone" value={form.phone} onChange={handleChange}
                      placeholder="+49 ..."
                      className={inputClass + " pl-9"} style={inputStyle}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wider">
                    Unternehmen <span className="text-gray-600 normal-case font-normal">(optional)</span>
                  </label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                    <input
                      type="text" name="company" value={form.company} onChange={handleChange}
                      placeholder="Firma / Hausverwaltung"
                      className={inputClass + " pl-9"} style={inputStyle}
                    />
                  </div>
                </div>
              </div>

              {/* Rolle + Einheiten */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wider">Rolle</label>
                  <div className="relative">
                    <select
                      name="role" value={form.role} onChange={handleChange}
                      className={inputClass + " appearance-none pr-8"}
                      style={inputStyle}
                    >
                      <option value="">Bitte wählen...</option>
                      {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wider">Anzahl Einheiten</label>
                  <div className="relative">
                    <select
                      name="units" value={form.units} onChange={handleChange}
                      className={inputClass + " appearance-none pr-8"}
                      style={inputStyle}
                    >
                      <option value="">Bitte wählen...</option>
                      {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
                  </div>
                </div>
              </div>

              {/* Aktuelle Software */}
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wider">
                  Aktuelle Software <span className="text-gray-600 normal-case font-normal">(optional)</span>
                </label>
                <input
                  type="text" name="current_software" value={form.current_software} onChange={handleChange}
                  placeholder="z.B. Domus, Immoware24, WinCasa, Excel, ..."
                  className={inputClass} style={inputStyle}
                />
              </div>

              {/* Größtes Problem */}
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wider">Größtes Problem</label>
                <div className="relative">
                  <select
                    name="main_problem" value={form.main_problem} onChange={handleChange}
                    className={inputClass + " appearance-none pr-8"}
                    style={inputStyle}
                  >
                    <option value="">Bitte wählen...</option>
                    {PROBLEMS.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
                </div>
              </div>

              {/* Gewünschter nächster Schritt */}
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wider">Gewünschter nächster Schritt</label>
                <div className="relative">
                  <select
                    name="desired_next_step" value={form.desired_next_step} onChange={handleChange}
                    className={inputClass + " appearance-none pr-8"}
                    style={inputStyle}
                  >
                    <option value="">Bitte wählen...</option>
                    {NEXT_STEPS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
                </div>
              </div>

              {error && (
                <div className="rounded-lg p-3 bg-red-500/15 border border-red-500/30">
                  <p className="text-red-400 text-xs">{error}</p>
                </div>
              )}

              <button
                type="submit" disabled={isSubmitting}
                className="w-full flex items-center justify-center gap-2 py-3.5 rounded-lg text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors mt-2"
              >
                {isSubmitting ? (
                  <><Loader2 className="h-4 w-4 animate-spin" />Anfrage wird gesendet...</>
                ) : (
                  <><ArrowRight className="h-4 w-4" />Pilotzugang anfragen</>
                )}
              </button>

              <p className="text-gray-600 text-xs text-center leading-relaxed">
                Keine automatische Produktfreigabe. Wir prüfen Ihre Anfrage und melden uns persönlich.
              </p>
            </form>
          </div>

          {/* Bereits Account */}
          <div className="mt-5 text-center">
            <p className="text-gray-600 text-xs">
              Bereits freigeschaltet?{' '}
              <Link to="/register" className="text-indigo-400 hover:text-indigo-300 transition-colors">
                Account erstellen
              </Link>
              {' '}oder{' '}
              <Link to="/login" className="text-indigo-400 hover:text-indigo-300 transition-colors">
                Anmelden
              </Link>
            </p>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="relative z-10 mt-8 flex items-center gap-2">
        <Shield className="w-3.5 h-3.5 text-gray-600 flex-shrink-0" />
        <p className="text-gray-600 text-xs text-center">
          Pilotbetrieb · Keine Rechts- oder Steuerberatung · Keine Aktion ohne Freigabe · Lokal bevorzugt
        </p>
      </div>
    </div>
  );
};

export default PilotRequestPage;
